import React, { useRef, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  SafeAreaView,
  KeyboardAvoidingView,
  Platform,
  Alert,
  Image,
  ActivityIndicator,
  Modal
} from 'react-native';
import { Link, useRouter, useLocalSearchParams } from 'expo-router';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Mail, Lock, Eye, EyeOff, AlertCircle, ChevronLeft, CheckCircle } from 'lucide-react-native';

export default function LoginScreen() {
  const router = useRouter();
  const { signedUp, email: emailFromQuery, mode, oobCode } = useLocalSearchParams<{
    signedUp?: string;
    email?: string;
    mode?: string;
    oobCode?: string;
  }>();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const { signIn, signInWithGoogle, resendVerificationEmail, verifyEmailCode } = useAuth();
  const { colors } = useTheme();

  // Modal State
  const [errorModalVisible, setErrorModalVisible] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState('');
  const [errorModalTitle, setErrorModalTitle] = useState('Erro');
  const [modalVariant, setModalVariant] = useState<'error' | 'info' | 'success'>('error');
  const [modalButtonText, setModalButtonText] = useState('Tentar Novamente');
  const afterModalRedirectRef = useRef<string | null>(null);
  const handledQueryRef = useRef<{ signedUp?: boolean; verifyEmail?: string }>({});

  const closeErrorModal = () => {
    setErrorModalVisible(false);
    const redirect = afterModalRedirectRef.current;
    afterModalRedirectRef.current = null;
    if (redirect) {
      router.replace(redirect as any);
    }
  };

  const showModal = (variant: 'error' | 'info' | 'success', title: string, message: string, buttonText?: string, redirectAfterClose?: string) => {
    setModalVariant(variant);
    setErrorModalTitle(title);
    setErrorModalMessage(message);
    setModalButtonText(buttonText || (variant === 'error' ? 'Tentar Novamente' : 'OK'));
    afterModalRedirectRef.current = redirectAfterClose || null;
    setErrorModalVisible(true);
  };

  React.useEffect(() => {
    if (Platform.OS !== 'web') return;
    if (signedUp === '1') {
      const initialEmail = typeof emailFromQuery === 'string' ? emailFromQuery : '';
      if (initialEmail) setEmail(initialEmail);
      if (!handledQueryRef.current.signedUp) {
        handledQueryRef.current.signedUp = true;
        showModal(
          'info',
          'Conta criada',
          'Enviamos um e-mail de verificação. Abra o link para ativar sua conta antes de fazer login.',
          'OK',
          '/(auth)/login'
        );
      }
    }
  }, [signedUp, emailFromQuery, router]);

  React.useEffect(() => {
    if (Platform.OS !== 'web') return;
    if (mode !== 'verifyEmail') return;
    if (!oobCode) return;

    if (handledQueryRef.current.verifyEmail === oobCode) return;
    handledQueryRef.current.verifyEmail = oobCode;

    let cancelled = false;
    const run = async () => {
      try {
        await verifyEmailCode(oobCode);
        if (cancelled) return;
        showModal(
          'success',
          'E-mail verificado',
          'Seu e-mail foi verificado com sucesso. Agora você já pode fazer login.',
          'OK',
          '/(auth)/login'
        );
      } catch (error: any) {
        if (cancelled) return;
        showModal(
          'info',
          'Link inválido ou expirado',
          'Se você já verificou seu e-mail, tente fazer login normalmente. Se não, solicite um novo e-mail de verificação na tela de login.',
          'Ir para login',
          '/(auth)/login'
        );
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [mode, oobCode, verifyEmailCode, router]);

  const handleLogin = async () => {
    if (!email || !password) {
      showErrorModal('Erro', 'Preencha todos os campos');
      return;
    }

    setLoading(true);
    try {
      await signIn(email, password);
    } catch (error: any) {
      console.error('Erro no signIn:', error);

      // Tratar diferentes tipos de erro
      if (error.code === 'auth/email-not-verified' || (error.message && error.message.toLowerCase().includes('e-mail não verificado'))) {
        Alert.alert(
          'Verifique seu E-mail',
          'Você precisa verificar seu e-mail antes de fazer login. Deseja reenviar o e-mail de verificação?',
          [
            { text: 'Cancelar', style: 'cancel' },
            {
              text: 'Reenviar',
              onPress: async () => {
                try {
                  await resendVerificationEmail();
                  Alert.alert('Sucesso', 'E-mail de verificação reenviado!');
                } catch (resendError: any) {
                  console.error('Erro ao reenviar e-mail:', resendError);
                  Alert.alert('Erro', resendError.message || 'Não foi possível reenviar o e-mail.');
                }
              },
            },
          ]
        );
      } else if (error.message && error.message.includes('password')) {
        showErrorModal('Erro de Login', 'Senha incorreta. Verifique sua senha e tente novamente.');
      } else if (error.message && error.message.includes('user-not-found')) {
        showErrorModal('Erro de Login', 'Usuário não encontrado. Verifique seu e-mail ou crie uma nova conta.');
      } else if (error.message && error.message.includes('too-many-requests')) {
        showErrorModal('Erro de Login', 'Muitas tentativas de login. Aguarde alguns minutos e tente novamente.');
      } else if (error.code === 'auth/invalid-credential') {
        showModal('error', 'Erro de Login', 'Credenciais inválidas. Verifique seu e-mail e senha.');
      } else {
        // Exibir qualquer outro erro que possa ocorrer
        showModal('error', 'Erro de Login', error.message || 'Ocorreu um erro durante o login. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    console.log('[LOGIN DEBUG] handleGoogleSignIn: Iniciando...');
    if (Platform.OS !== 'web') {
      Alert.alert('Indisponível', 'Login com Google está disponível apenas na versão web por enquanto.');
      return;
    }

    setGoogleLoading(true);
    try {
      console.log('[LOGIN DEBUG] Chamando signInWithGoogle...');
      await signInWithGoogle();
      console.log('[LOGIN DEBUG] signInWithGoogle retornou sem erro');
    } catch (error: any) {
      console.error('[LOGIN DEBUG] ERRO capturado:', {
        message: error?.message,
        code: error?.code,
        name: error?.name,
        stack: error?.stack?.substring(0, 300)
      });
      if (error.message) {
        showErrorModal('Erro', error.message);
      } else {
        showErrorModal('Erro', 'Erro desconhecido no login. Verifique o console.');
      }
    } finally {
      console.log('[LOGIN DEBUG] handleGoogleSignIn: Finalizado');
      setGoogleLoading(false);
    }
  };

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    backButton: {
      position: 'absolute',
      top: 24,
      left: 24,
      padding: 12,
      borderRadius: 24,
      zIndex: 10,
    },
    keyboardView: {
      flex: 1,
    },
    content: {
      flex: 1,
      paddingHorizontal: 24,
      justifyContent: 'center',
    },
    header: {
      alignItems: 'center',
      marginBottom: 48,
    },
    logo: {
      width: 80,
      height: 80,
      marginBottom: 16,
    },
    title: {
      fontSize: 28,
      fontFamily: 'Inter-Bold',
      color: colors.text,
      marginBottom: 8,
    },
    subtitle: {
      fontSize: 16,
      fontFamily: 'Inter-Regular',
      color: colors.textSecondary,
      textAlign: 'center',
      lineHeight: 24,
    },
    form: {
      gap: 16,
    },
    inputContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: colors.surface,
      borderRadius: 12,
      paddingHorizontal: 16,
      height: 56,
      borderWidth: 1,
      borderColor: colors.border,
    },
    inputIcon: {
      marginRight: 12,
    },
    input: {
      flex: 1,
      fontSize: 16,
      fontFamily: 'Inter-Regular',
      color: colors.text,
    },
    passwordInput: {
      paddingRight: 40,
    },
    eyeIcon: {
      position: 'absolute',
      right: 16,
      padding: 4,
    },
    loginButton: {
      backgroundColor: colors.primary,
      borderRadius: 12,
      height: 56,
      justifyContent: 'center',
      alignItems: 'center',
      marginTop: 8,
    },
    loginButtonDisabled: {
      opacity: 0.6,
    },
    loginButtonText: {
      fontSize: 16,
      fontFamily: 'Inter-SemiBold',
      color: '#FFFFFF',
    },
    forgotPasswordContainer: {
      alignItems: 'center',
      marginTop: 12,
    },
    forgotPasswordLink: {
      fontSize: 14,
      fontFamily: 'Inter-Regular',
      color: colors.primary,
    },
    registerContainer: {
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      marginTop: 24,
    },
    registerText: {
      fontSize: 14,
      fontFamily: 'Inter-Regular',
      color: colors.textSecondary,
    },
    registerLink: {
      fontSize: 14,
      fontFamily: 'Inter-SemiBold',
      color: colors.primary,
    },
    dividerContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      marginVertical: 20,
    },
    dividerLine: {
      flex: 1,
      height: 1,
      backgroundColor: colors.border,
    },
    dividerText: {
      fontSize: 14,
      fontFamily: 'Inter-Regular',
      color: colors.textSecondary,
      marginHorizontal: 16,
    },
    googleButton: {
      backgroundColor: '#FFFFFF',
      borderRadius: 12,
      height: 56,
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      borderWidth: 1,
      borderColor: colors.border,
    },
    googleButtonDisabled: {
      opacity: 0.6,
    },
    googleIcon: {
      width: 20,
      height: 20,
      marginRight: 12,
    },
    googleButtonText: {
      fontSize: 16,
      fontFamily: 'Inter-SemiBold',
      color: '#1F1F1F',
    },
    // Modal Styles
    modalOverlay: {
      flex: 1,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      justifyContent: 'center',
      alignItems: 'center',
      padding: 24,
    },
    modalContent: {
      width: '100%',
      maxWidth: 320,
      borderRadius: 24,
      padding: 24,
      alignItems: 'center',
      elevation: 5,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.25,
      shadowRadius: 3.84,
    },
    modalIconContainer: {
      marginBottom: 16,
    },
    modalTitle: {
      fontSize: 20,
      fontFamily: 'Inter-Bold',
      marginBottom: 8,
      textAlign: 'center',
    },
    modalMessage: {
      fontSize: 14,
      fontFamily: 'Inter-Regular',
      textAlign: 'center',
      marginBottom: 24,
      lineHeight: 20,
    },
    modalButton: {
      width: '100%',
      paddingVertical: 12,
      borderRadius: 12,
      alignItems: 'center',
    },
    modalButtonText: {
      fontSize: 16,
      fontFamily: 'Inter-SemiBold',
      color: '#FFFFFF',
    },
  });

  return (
    <SafeAreaView style={styles.container}>
      <TouchableOpacity
        style={[styles.backButton, { backgroundColor: colors.surface }]}
        activeOpacity={0.7}
        onPress={() => router.push('/')}
      >
        <ChevronLeft size={20} color={colors.text} />
      </TouchableOpacity>
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.keyboardView}
      >
        <View style={styles.content}>
          <View style={styles.header}>
            <Image
              source={require('@/public/icon-pages.png')}
              style={styles.logo}
              resizeMode="contain"
            />
            <Text style={styles.title}>Põe no Racha</Text>
            <Text style={styles.subtitle}>Divida contas de forma simples e transparente</Text>
          </View>

          <View style={styles.form}>
            <View style={styles.inputContainer}>
              <Mail size={20} color={colors.textSecondary} style={styles.inputIcon} />
              <TextInput
                style={styles.input}
                placeholder="Email"
                placeholderTextColor={colors.textSecondary}
                value={email}
                onChangeText={setEmail}
                keyboardType="email-address"
                autoCapitalize="none"
              />
            </View>

            <View style={styles.inputContainer}>
              <Lock size={20} color={colors.textSecondary} style={styles.inputIcon} />
              <TextInput
                style={[styles.input, styles.passwordInput]}
                placeholder="Senha"
                placeholderTextColor={colors.textSecondary}
                value={password}
                onChangeText={setPassword}
                secureTextEntry={!showPassword}
              />
              <TouchableOpacity
                onPress={() => setShowPassword(!showPassword)}
                style={styles.eyeIcon}
              >
                {showPassword ? (
                  <EyeOff size={20} color={colors.textSecondary} />
                ) : (
                  <Eye size={20} color={colors.textSecondary} />
                )}
              </TouchableOpacity>
            </View>

            <TouchableOpacity
              style={[styles.loginButton, loading && styles.loginButtonDisabled]}
              onPress={handleLogin}
              disabled={loading || googleLoading}
            >
              <Text style={styles.loginButtonText}>
                {loading ? 'Entrando...' : 'Entrar'}
              </Text>
            </TouchableOpacity>

            {/* Esqueci minha senha */}
            <View style={styles.forgotPasswordContainer}>
              <Link href="/(auth)/forgot-password" asChild>
                <TouchableOpacity>
                  <Text style={styles.forgotPasswordLink}>Esqueci minha senha</Text>
                </TouchableOpacity>
              </Link>
            </View>

            {/* Separador */}
            <View style={styles.dividerContainer}>
              <View style={styles.dividerLine} />
              <Text style={styles.dividerText}>ou</Text>
              <View style={styles.dividerLine} />
            </View>

            {/* Botão Google */}
            <TouchableOpacity
              style={[styles.googleButton, googleLoading && styles.googleButtonDisabled]}
              onPress={handleGoogleSignIn}
              disabled={loading || googleLoading}
              activeOpacity={0.7}
            >
              {googleLoading ? (
                <ActivityIndicator size="small" color="#1F1F1F" />
              ) : (
                <>
                  <Image
                    source={{ uri: 'https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg' }}
                    style={styles.googleIcon}
                    resizeMode="contain"
                  />
                  <Text style={styles.googleButtonText}>Entrar com Google</Text>
                </>
              )}
            </TouchableOpacity>

            <View style={styles.registerContainer}>
              <Text style={styles.registerText}>Não tem uma conta? </Text>
              <Link href="/(auth)/register" asChild>
                <TouchableOpacity>
                  <Text style={styles.registerLink}>Cadastre-se</Text>
                </TouchableOpacity>
              </Link>
            </View>
          </View>
        </View>
      </KeyboardAvoidingView>

      {/* Error Modal */}
      <Modal
        animationType="fade"
        transparent={true}
        visible={errorModalVisible}
        onRequestClose={closeErrorModal}
      >
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { backgroundColor: colors.surface }]}>
            <View style={styles.modalIconContainer}>
              {modalVariant === 'success' ? (
                <CheckCircle size={48} color={colors.success} />
              ) : (
                <AlertCircle size={48} color={modalVariant === 'error' ? colors.error : colors.primary} />
              )}
            </View>
            <Text style={[styles.modalTitle, { color: colors.text }]}>{errorModalTitle}</Text>
            <Text style={[styles.modalMessage, { color: colors.textSecondary }]}>{errorModalMessage}</Text>
            <TouchableOpacity
              style={[styles.modalButton, { backgroundColor: colors.primary }]}
              onPress={closeErrorModal}
            >
              <Text style={styles.modalButtonText}>{modalButtonText}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
}
